import React, { useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';

import useFiles from '@hooks/useFiles';
import Loading from '@components/Loading';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import { IFile } from '@routes/Common/type';
import { RfqDetailData } from '@routes/Common/type';
import { useStylesCommon } from './useStyles';
import { Download } from '@carbon/icons-react';

interface UseStylesProps {
  matches?: boolean;
}

const useStyles = makeStyles(() => {
  return {
    boxTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
      '& svg': {
        fontSize: '20px !important'
      }
    },
    displayItemHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 36,
      borderBottom: `1px solid ${colorSet.gray200}`,
      '& :nth-child(1)': {
        width: 'calc(100% - 137px)'
      },
      '& :nth-child(2)': {
        width: 137
      }
    },
    displayItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 0px',
      height: 100,
      '& .MuiTypography-colorTextSecondary': {
        color: colorSet.gray600
      },
      borderBottom: `1px solid ${colorSet.gray200}`
    },
    itemIndex: {
      width: 24
    },
    thumbnail: {
      width: 66,
      height: 66,
      border: `1px solid ${colorSet.gray200}`,
      borderRadius: 8
    },
    fileInfo: {
      display: 'flex',
      flexDirection: (props: UseStylesProps) =>
        props.matches ? 'column' : 'row',
      alignItems: (props: UseStylesProps) =>
        props.matches ? 'flex-start' : 'center',
      width: (props: UseStylesProps) =>
        props.matches ? 'calc(100% - 23px)' : 'calc(100% - 140px)',
      paddingLeft: (props: UseStylesProps) => (props.matches ? 12 : 16),
      '& .MuiSvgIcon-root': {
        cursor: 'pointer',
        color: colorSet.gray700,
        marginLeft: 4
      }
    },
    fileNameWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: (props: UseStylesProps) =>
        props.matches ? '100%' : 'calc(100% - 20px)'
    },
    fileName: {
      display: 'flex',
      alignItems: 'center',
      width: (props: UseStylesProps) => (props.matches ? 185 : '100%'),
      '& > p': {
        marginRight: 4
      },
      '& svg': {
        minWidth: 20,
        '&:hover': {
          cursor: 'pointer'
        }
      }
    },
    material: {
      width: (props: UseStylesProps) => (props.matches ? '100%' : 137),
      padding: (props: UseStylesProps) => (props.matches ? 0 : '0px 8px')
    },
    quantity: {
      width: 140
    },
    totalQuantity: {
      '& span': {
        color: colorSet.primary500
      },
      marginTop: (props: UseStylesProps) => (props.matches ? 12 : 16)
    },
    publicClose: {
      width: '100%',
      height: 44,
      borderRadius: 8,
      marginBottom: 16,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colorSet.errorBackGround,
      '& > svg': {
        marginRight: 8
      },
      '& > p': {
        color: colorSet.errorMain
      }
    }
  };
});

interface Props {
  data?: RfqDetailData;
  matches?: boolean;
}

const RfqPcbAssembleFilesPresenter: React.FC<Props> = ({ data, matches }) => {
  const classes = useStyles({ matches: matches });
  const classesCommon = useStylesCommon({ matches: matches });
  const { enqueueSnackbar } = useSnackbar();
  const { onDownloadFile, onDownloadZipFile } = useFiles();
  const [isLoading, setIsLoading] = useState(false);
  const assemblyFiles = data?.pcb?.assemblyFiles ?? [];

  const onFileDownLoad = (file: IFile) => {
    onDownloadFile(file)
      .then((resp) => {
        if (!resp) {
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        }
      })
      .catch(() => {
        enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
          variant: 'error'
        });
      });
  };

  const onFileDownloadAll = () => {
    if (assemblyFiles.length < 1) return false;

    const fileArr: Array<IFile> = [];
    setIsLoading(true);

    assemblyFiles.map((file) => {
      if (file.fileId && file.fileName) {
        fileArr.push({ fileId: file.fileId, fileName: file.fileName });
      }
      return null;
    });

    if (fileArr.length > 1) {
      onDownloadZipFile(fileArr)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        });
    } else if (fileArr.length === 1) {
      setIsLoading(false);
      onFileDownLoad(fileArr[0]);
    }
  };

  return (
    <React.Fragment>
      <Loading open={isLoading} />
      <Box className={classesCommon.root + ' item'}>
        <div className={classes.boxTitle}>
          <CustomizedTypography
            variant={'h4'}
            color="textPrimary"
            fontWeight="500"
          >
            조립파일
          </CustomizedTypography>
          {assemblyFiles.length > 0 && (
            <CustomizedButton
              variant="text"
              size="large"
              startIcon={<Download />}
              onClick={onFileDownloadAll}
            >
              전체 다운로드
            </CustomizedButton>
          )}
        </div>
        {assemblyFiles.length > 0 ? (
          <>
            <div className={classes.displayItemHeader}>
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                항목
              </CustomizedTypography>

              <CustomizedTypography
                variant="subtitle2"
                color="textSecondary"
                align="right"
              >
                조립수량(PCS)
              </CustomizedTypography>
            </div>
            {assemblyFiles.map((file, index) => {
              const fileId = file.fileId;
              const fileName = file.fileName;
              const quantity = file.quantity ?? 0;

              return (
                <div key={fileId} className={classes.displayItem}>
                  <CustomizedTypography
                    className={classes.itemIndex}
                    variant="body2"
                    color="textSecondary"
                  >
                    {index + 1}
                  </CustomizedTypography>
                  <div className={classes.fileInfo}>
                    <div className={classes.fileNameWrapper}>
                      <div className={classes.fileName}>
                        <CustomizedTypography
                          variant="body1"
                          color="textPrimary"
                          gutterBottoms={matches ? 4 : 0}
                          noWrap
                        >
                          {fileName}
                        </CustomizedTypography>
                        <Download
                          width={20}
                          height={20}
                          color={colorSet.primary500}
                          onClick={() => {
                            onFileDownLoad(file);
                          }}
                        />
                      </div>
                      {matches && (
                        <CustomizedTypography
                          className={classes.quantity}
                          variant="body1"
                          align="right"
                          fontWeight="500"
                        >
                          {quantity.toLocaleString('kr')}개
                        </CustomizedTypography>
                      )}
                    </div>
                  </div>
                  {!matches && (
                    <CustomizedTypography
                      className={classes.quantity}
                      variant="body1"
                      align="right"
                      fontWeight="500"
                    >
                      {quantity.toLocaleString('kr')}개
                    </CustomizedTypography>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <CustomizedTypography variant={'body1'}>
            조립 파일이 없습니다.
          </CustomizedTypography>
        )}
      </Box>
    </React.Fragment>
  );
};

export default RfqPcbAssembleFilesPresenter;
